import { createSelector } from 'reselect';

import type APIMessage from '~/types/APIMessage';
import type OrdersBatch from '~/types/OrdersBatch';

import { getOrdersData } from '~/selectors/orders.selectors';
import { type ApplicationState } from '~/store';
import { type OrderSelection } from '~/types/Order';
import { type BatchEntry, type BatchSteps, type BatchTransaction } from '~/types/OrdersBatch';
import { BatchEntryStatus } from '~/types/OrdersBatch';
import { TransactionStatus } from '~/types/Transaction';

import { getTransactionsData } from './transactions.selectors';

export const getBatchStore = (state: ApplicationState) => state.ordersBatch;

export const getBatchData = createSelector(
  getBatchStore,
  (batchStore): Maybe<Partial<OrdersBatch>> => batchStore.data,
);

const getBatchOrders = createSelector(getBatchStore, (batchStore) => batchStore.orders);

export const getBatchOrdersLength = createSelector(
  getBatchOrders,
  (orders): number => orders.allObjectIds.length,
);

export const getBatchEntries = createSelector(
  getBatchStore,
  (batchStore) => batchStore.batchEntries,
);

export const getBatchShipments = createSelector(
  getBatchStore,
  (batchStore) => batchStore.shipments,
);

export const getBatchTransactions = createSelector(
  getBatchStore,
  (batchStore) => batchStore.transactions,
);

const getBatchEntriesData = createSelector(getBatchEntries, (batchEntries) => batchEntries.data);

export const getBatchTransactionsData = createSelector(
  getBatchTransactions,
  (batchTransactions) => batchTransactions.data,
);

export const getBatchId = createSelector(getBatchData, (batchData) =>
  batchData ? batchData.id : null,
);

export const getBatchCurrentStep = createSelector(
  getBatchStore,
  (batchStore): BatchSteps => batchStore.currentStep,
);

export const getWasOrdersFetchTriggered = createSelector(
  getBatchStore,
  (batchStore): boolean => batchStore.wasOrdersFetchTriggered,
);

export const getSuccessfulBatchTransactions = createSelector(
  getBatchTransactions,
  (batchTransactions): BatchTransaction[] =>
    Object.values(batchTransactions.data).filter(
      (batchTransaction) => batchTransaction.objectStatus === TransactionStatus.SUCCESS,
    ),
);

export const getEntriesWithSuccessfulTransactions = createSelector(
  getBatchEntries,
  getSuccessfulBatchTransactions,
  (batchEntries, batchTransactions) =>
    batchTransactions
      .map((batchTransaction) => ({
        batchEntryId: batchTransaction.batchEntryId,
        orderObjectId: batchEntries.data[batchTransaction.batchEntryId].orderObjectId,
      }))
      .reduce<{
        batchEntryIds: number[];
        orderObjectIds: string[];
        orderSelection: OrderSelection;
      }>(
        (entries, { batchEntryId, orderObjectId }) => {
          entries.orderObjectIds.push(orderObjectId);
          entries.batchEntryIds.push(batchEntryId);
          if (entries.orderSelection[orderObjectId] === undefined)
            entries.orderSelection = { ...entries.orderSelection, [orderObjectId]: [] };

          entries.orderSelection[orderObjectId].push(batchEntryId);

          return entries;
        },
        {
          batchEntryIds: [],
          orderObjectIds: [],
          orderSelection: {},
        },
      ),
);

export const getFailedTransactionBatchEntries = createSelector(
  getBatchEntriesData,
  getBatchTransactions,
  getBatchTransactionsData,
  (batchEntriesData, transactions, transactionsData): BatchEntry[] =>
    Object.values(batchEntriesData).filter(
      (batchEntry) =>
        [BatchEntryStatus.RATE_NOT_AVAILABLE, BatchEntryStatus.TRANSACTION_REQUEST_FAILED].includes(
          batchEntry.objectStatus,
        ) ||
        (transactions.byBatchEntryId[batchEntry.id] &&
          transactionsData[transactions.byBatchEntryId[batchEntry.id][0]].objectStatus ===
            TransactionStatus.ERROR),
    ),
);

/**
 * Returns only the failed batch entries that have an order number
 */
export const getFailedBatchEntriesWithOrderNumbers = createSelector(
  getFailedTransactionBatchEntries,
  getOrdersData,
  (failedBatchEntries, ordersData): BatchEntry[] =>
    Object.values(failedBatchEntries).filter(
      (entry) => ordersData[entry.orderObjectId]?.orderNumber,
    ),
);

export const GENERIC_PURCHASE_ERROR_MESSAGE =
  'There was an error processing your request. Please try again later.';

/**
 * Returns messages for failed transaction batch entries mapped by order ID.
 *
 * If batch entry is associated with transaction, only return messages for fetched transaction.
 */
export const getFailedBatchEntriesMessages = createSelector(
  getFailedTransactionBatchEntries,
  getBatchTransactions,
  getTransactionsData,
  (failedBatchEntries, batchTransactions, transactionsData): Record<string, APIMessage[]> =>
    failedBatchEntries.reduce(
      (messagesByOrderId, batchEntry) => {
        let messages: APIMessage[] = [];

        // Transaction complete but no transaction data available yet
        if (
          batchEntry.objectStatus === BatchEntryStatus.COMPLETE_TRANSACTION &&
          (!batchTransactions.byBatchEntryId[batchEntry.id] ||
            !transactionsData[batchTransactions.byBatchEntryId[batchEntry.id][0]])
        )
          return messagesByOrderId;

        if (
          [
            BatchEntryStatus.RATE_NOT_AVAILABLE,
            BatchEntryStatus.TRANSACTION_REQUEST_FAILED,
          ].includes(batchEntry.objectStatus)
        ) {
          messages = [
            {
              code: batchEntry.objectStatus,
              kind: 'APIMessage',
              source: 'batch-entry',
              text:
                batchEntry.objectStatus === BatchEntryStatus.TRANSACTION_REQUEST_FAILED
                  ? GENERIC_PURCHASE_ERROR_MESSAGE
                  : 'Could not auto create return.',
            } as APIMessage,
          ];
        } else {
          messages = transactionsData[batchTransactions.byBatchEntryId[batchEntry.id][0]]
            ? transactionsData[batchTransactions.byBatchEntryId[batchEntry.id][0]].messages
            : [];
        }

        return messages
          ? {
              ...messagesByOrderId,
              [batchEntry.orderObjectId]: messages,
            }
          : messagesByOrderId;
      },
      {} as Record<string, APIMessage[]>,
    ),
);
